import React, { useEffect, useMemo, useState } from "react";
import Dropdown from "../../common/dropDown";
import { filterAssignablePermissionKeys } from "../../../constants/permissions";
import { isSystemAdministratorRole } from "../../../utils/permissionUtils";
import { buildRolePermissionTreeGroups } from "../../../utils/rolePermissionTree";
import RolePermissionTree, {
  PermissionInfoIcon,
} from "./RolePermissionTree";
import "./RolePermissionTree.scss";
import "./userManagement.scss";

function getSelectedRoleId(role) {
  if (role == null || role === "") return "";
  if (typeof role === "object") return String(role.value ?? "").trim();
  return String(role).trim();
}

function getSelectedRoleLabel(role, roles = []) {
  if (typeof role === "object" && role?.label) return String(role.label).trim();
  const roleId = getSelectedRoleId(role);
  const match = roles.find(
    (item) => String(item.value).toLowerCase() === roleId.toLowerCase(),
  );
  return match?.label ? String(match.label).trim() : "";
}

function resolveRoleDropdownSelection(role, roles = [], roleOptions = []) {
  const options = roleOptions.length ? roleOptions : roles;
  if (typeof role === "object" && role !== null && role.value) {
    return role;
  }
  const roleId = getSelectedRoleId(role);
  if (!roleId) return null;
  return (
    options.find(
      (item) => String(item.value).toLowerCase() === roleId.toLowerCase(),
    ) || null
  );
}

function getGroupKeys(group) {
  return (group?.permissions || []).map((item) => item.key).filter(Boolean);
}

function getGroupSelectedCount(group, selectedPermissions, showFullAccessSelected) {
  const keys = getGroupKeys(group);
  if (showFullAccessSelected) return keys.length;
  return keys.filter((key) => selectedPermissions.includes(key)).length;
}

function UserRolePermissionPanel({
  role,
  roles = [],
  roleOptions = [],
  onRoleSelect,
  userPermissions = [],
  onUserPermissionsChange,
  userData,
  canEdit = false,
  canEditRole = false,
  /** "tree" (Add User) | "modules" (Profile — module list + permissions) */
  layout = "tree",
  hideRoleDropdown = false,
  showDepartment = false,
  departmentOptions = [],
  selectedDepartment = null,
  onDepartmentSelect,
  departmentDisabled = false,
}) {
  const dropdownOptions = roleOptions.length ? roleOptions : roles;
  const selectedRoleDropdown = resolveRoleDropdownSelection(
    role,
    roles,
    dropdownOptions,
  );
  const selectedRoleId = getSelectedRoleId(role);
  const selectedRoleLabel = getSelectedRoleLabel(role, roles);
  const isSystemAdminRole = isSystemAdministratorRole(selectedRoleLabel);
  const canEditPermissions = Boolean(
    selectedRoleId && canEdit && !isSystemAdminRole,
  );
  const useModuleSplit = layout === "modules";

  const permissionTreeGroups = useMemo(
    () => buildRolePermissionTreeGroups(userData),
    [userData],
  );

  const [activeModuleId, setActiveModuleId] = useState("");

  useEffect(() => {
    if (!permissionTreeGroups.length) {
      setActiveModuleId("");
      return;
    }
    setActiveModuleId((prev) => {
      if (prev && permissionTreeGroups.some((group) => group.id === prev)) {
        return prev;
      }
      return permissionTreeGroups[0].id;
    });
  }, [permissionTreeGroups]);

  const activeGroup = useMemo(
    () =>
      permissionTreeGroups.find((group) => group.id === activeModuleId) ||
      permissionTreeGroups[0] ||
      null,
    [permissionTreeGroups, activeModuleId],
  );

  const selectedPermissions = useMemo(
    () => filterAssignablePermissionKeys(userPermissions),
    [userPermissions],
  );

  const activeKeys = useMemo(() => getGroupKeys(activeGroup), [activeGroup]);

  const activeSelectedCount = useMemo(
    () =>
      getGroupSelectedCount(
        activeGroup,
        selectedPermissions,
        isSystemAdminRole,
      ),
    [activeGroup, selectedPermissions, isSystemAdminRole],
  );

  const allActiveSelected =
    isSystemAdminRole ||
    (activeKeys.length > 0 &&
      activeKeys.every((key) => selectedPermissions.includes(key)));

  const setSelectedPermissions = (updater) => {
    if (!onUserPermissionsChange) return;
    const next =
      typeof updater === "function" ? updater(selectedPermissions) : updater;
    onUserPermissionsChange(filterAssignablePermissionKeys(next));
  };

  const togglePermission = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  const selectManyPermissions = (keys = []) => {
    const normalized = Array.isArray(keys) ? keys : [];
    if (!normalized.length) return;
    setSelectedPermissions((prev) =>
      Array.from(new Set([...prev, ...normalized])),
    );
  };

  const deselectManyPermissions = (keys = []) => {
    const normalized = Array.isArray(keys) ? keys : [];
    if (!normalized.length) return;
    setSelectedPermissions((prev) =>
      prev.filter((item) => !normalized.includes(item)),
    );
  };

  const roleDropdown = (
    <div className="permission-module-split__role">
      <label className="permission-module-split__role-label">Role</label>
      <Dropdown
        options={dropdownOptions}
        isMulti={false}
        selected={selectedRoleDropdown}
        onSelect={onRoleSelect}
        disabled={!canEditRole}
      />
    </div>
  );

  const departmentDropdown =
    showDepartment && typeof onDepartmentSelect === "function" ? (
      <div className="permission-module-split__role">
        <label className="permission-module-split__role-label">Department</label>
        <Dropdown
          options={departmentOptions}
          isMulti={false}
          selected={selectedDepartment}
          onSelect={onDepartmentSelect}
          disabled={departmentDisabled}
        />
      </div>
    ) : null;

  const roleDepartmentRow =
    departmentDropdown ? (
      <div className="permission-module-split__fields">
        {departmentDropdown}
        {hideRoleDropdown ? null : roleDropdown}
      </div>
    ) : null;

  return (
    <div
      className={[
        "user-role-permission-panel",
        useModuleSplit ? "user-role-permission-panel--modules" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {!hideRoleDropdown && !useModuleSplit ? (
        <Dropdown
          options={dropdownOptions}
          isMulti={false}
          selected={selectedRoleDropdown}
          onSelect={onRoleSelect}
          disabled={!canEditRole}
        />
      ) : null}

      {!selectedRoleId && !useModuleSplit ? (
        <div className="user-role-permission-panel__empty">
          <i className="fas fa-shield-alt" aria-hidden="true" />
          <p>Select a role, then assign permissions for this user.</p>
        </div>
      ) : null}

      {useModuleSplit ? roleDepartmentRow : null}

      {useModuleSplit ? (
        <div className="permission-module-split">
          <aside
            className="permission-module-split__nav"
            aria-label="Permission modules"
          >
            {hideRoleDropdown || roleDepartmentRow ? null : roleDropdown}
            {!selectedRoleId ? (
              <div className="permission-module-split__nav-empty">
                Select a role to assign permissions.
              </div>
            ) : null}
            {selectedRoleId
              ? permissionTreeGroups.map((group) => {
              const total = getGroupKeys(group).length;
              const selected = getGroupSelectedCount(
                group,
                selectedPermissions,
                isSystemAdminRole,
              );
              const isActive = group.id === (activeGroup?.id || "");
                  return (
                    <button
                      key={group.id}
                      type="button"
                      className={[
                        "permission-module-split__nav-item",
                        isActive ? "is-active" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => setActiveModuleId(group.id)}
                    >
                      <i
                        className={`fas ${group.icon || "fa-cube"}`}
                        style={{ color: group.color || "#64748b" }}
                        aria-hidden="true"
                      />
                      <span className="permission-module-split__nav-label">
                        {group.label}
                      </span>
                      <span className="permission-module-split__nav-count">
                        {selected}/{total}
                      </span>
                    </button>
                  );
                })
              : null}
          </aside>

          <div className="permission-module-split__detail">
            {selectedRoleId && activeGroup ? (
              <>
                <div className="permission-module-split__detail-head">
                  <div className="permission-module-split__detail-title">
                    <i
                      className={`fas ${activeGroup.icon || "fa-cube"}`}
                      style={{ color: activeGroup.color || "#64748b" }}
                      aria-hidden="true"
                    />
                    <span>{activeGroup.label}</span>
                    <span className="permission-module-split__nav-count">
                      {activeSelectedCount}/{activeKeys.length}
                    </span>
                  </div>
                  <label className="permission-module-split__bulk">
                    <input
                      type="checkbox"
                      checked={allActiveSelected}
                      disabled={
                        !canEditPermissions ||
                        isSystemAdminRole ||
                        !activeKeys.length
                      }
                      onChange={() => {
                        if (!canEditPermissions || isSystemAdminRole) return;
                        if (allActiveSelected) {
                          deselectManyPermissions(activeKeys);
                        } else {
                          selectManyPermissions(activeKeys);
                        }
                      }}
                    />
                    <span>
                      {allActiveSelected ? "Unselect all" : "Select all"}
                    </span>
                  </label>
                </div>

                <div
                  className="permission-module-split__list"
                  role="group"
                  aria-label={`${activeGroup.label} permissions`}
                >
                  {(activeGroup.permissions || []).map((permission) => {
                    const checked =
                      isSystemAdminRole ||
                      selectedPermissions.includes(permission.key);
                    return (
                      <div
                        key={permission.key}
                        className="permission-module-split__item"
                      >
                        <label className="permission-module-split__check">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!canEditPermissions || isSystemAdminRole}
                            onChange={() => togglePermission(permission.key)}
                          />
                          <span>{permission.label}</span>
                        </label>
                        <PermissionInfoIcon permission={permission} />
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="permission-module-split__empty">
                {selectedRoleId
                  ? "No modules available."
                  : "Select a role to view permissions."}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {selectedRoleId && !useModuleSplit ? (
        <RolePermissionTree
          groups={permissionTreeGroups}
          selectedPermissions={selectedPermissions}
          onTogglePermission={togglePermission}
          onSelectMany={selectManyPermissions}
          onDeselectMany={deselectManyPermissions}
          canEdit={canEditPermissions}
          showAllSelected={isSystemAdminRole}
        />
      ) : null}
    </div>
  );
}

export default UserRolePermissionPanel;
